import { AbstractTranscriptEditorCommand } from './AbstractTranscriptEditorCommand';
import { TranscriptEditorCore } from '../TranscriptEditorCore';
import { TranscriptEditorChangeType } from '../types';

/**
 * Interface for tracking affected elements during text replacement
 */
interface AffectedElement {
  segmentId: string;
  paragraphId: string;
  elementId: string;
  originalValue: string;
  newValue: string;
}

/**
 * Command to replace text globally throughout the transcript
 * 
 * This command searches for all instances of a text string across all elements
 * in the transcript and replaces them with the specified replacement text.
 * It tracks all affected elements for undo operations.
 */
export class ReplaceTextGloballyCommand extends AbstractTranscriptEditorCommand {
  /** Original text to find and replace */
  private readonly originalText: string;
  
  /** Text to replace the original text with */
  private readonly replacementText: string;
  
  /** Reference to the editor core */
  private readonly editorCore: TranscriptEditorCore;
  
  /** List of elements that were modified */
  private affectedElements: AffectedElement[] = [];
  
  /** Total count of individual text replacements */
  public replacementCount: number = 0;
  
  /** ID of the user performing the operation */
  private readonly userId: string;

  /**
   * Create a new global text replacement command
   * 
   * @param editor - The transcript editor instance
   * @param originalText - The text to search for and replace
   * @param replacementText - The text to replace with
   * @param userId - The ID of the user performing the operation
   */
  constructor(
    editor: TranscriptEditorCore,
    originalText: string,
    replacementText: string,
    userId: string
  ) {
    // Create descriptive command name
    const displayOriginal = originalText.length > 10 
      ? `${originalText.slice(0, 10)}...` 
      : originalText;
    const displayReplacement = replacementText.length > 10 
      ? `${replacementText.slice(0, 10)}...` 
      : replacementText;
    
    super(`Replace "${displayOriginal}" → "${displayReplacement}" globally`, editor);
    
    this.originalText = originalText;
    this.replacementText = replacementText;
    this.editorCore = editor;
    this.userId = userId;
    
    // Store metadata for debugging and tracking
    this.setMetadata('originalText', originalText);
    this.setMetadata('replacementText', replacementText);
    this.setMetadata('userId', userId);
  }

  /**
   * Executes the global text replacement command
   * 
   * @returns Promise resolving to true if successful
   */
  public async execute(): Promise<boolean> {
    try {
      // Reset state for re-execution
      this.affectedElements = [];
      this.replacementCount = 0;

      // Start batch operation for better performance
      this.editorCore.beginBatch();

      // Get container
      const container = this.editorCore.getContainer();
      if (!container) {
        return false;
      }

      // Create regex for case-insensitive global replacement
      const regex = new RegExp(this.escapeRegExp(this.originalText), 'gi');

      // Scan all elements first to collect affected elements
      for (const segment of container.segments) {
        for (const paragraph of segment.paragraphs) {
          for (const element of paragraph.elements) {
            // Only process text elements
            if (element.type === 'text' && element.value) {
              const originalValue = element.value;
              
              // Test if the element contains the search text
              if (regex.test(originalValue)) {
                // Reset the regex state (reset lastIndex)
                regex.lastIndex = 0;
                
                // Create the new value with the replacement
                const newValue = originalValue.replace(regex, this.replacementText);
                
                // If the text changed, add to affected elements
                if (newValue !== originalValue) {
                  // Count the number of replacements in this element
                  regex.lastIndex = 0; // Reset regex again for matching
                  const matchCount = (originalValue.match(regex) || []).length;
                  this.replacementCount += matchCount;
                  
                  // Add to affected elements
                  this.affectedElements.push({
                    segmentId: segment.id,
                    paragraphId: paragraph.id,
                    elementId: element.id,
                    originalValue,
                    newValue
                  });
                  
                }
              }
            }
          }
        }
      }

      // If no elements are affected, return false
      if (this.affectedElements.length === 0) {
        return false;
      }
      
      // Store metadata about affected elements
      this.setMetadata('affectedElementCount', this.affectedElements.length);
      this.setMetadata('replacementCount', this.replacementCount);

      // Now update all affected elements
      for (const affected of this.affectedElements) {
        this.editorCore.updateElementText(
          affected.segmentId,
          affected.paragraphId,
          affected.elementId,
          affected.newValue,
          this.userId
        );
      }

      // Record the operation in the editor's history
      await this.editorCore.recordOperation({
        type: 'text_edit',
        userId: this.userId,
        timestamp: Date.now(),
        data: {
          originalText: this.originalText,
          replacementText: this.replacementText,
          affectedElements: this.affectedElements.map(e => ({
            segmentId: e.segmentId,
            paragraphId: e.paragraphId,
            elementId: e.elementId
          })),
          replacementCount: this.replacementCount
        }
      });

      // Notify observers about the global text replacement
      this.editorCore.notifyObservers(TranscriptEditorChangeType.TEXT_REPLACED_GLOBAL, {
        originalText: this.originalText,
        replacementText: this.replacementText,
        affectedElementsCount: this.affectedElements.length,
        replacementCount: this.replacementCount
      });
      
      return true;
    } catch (error) {
      console.error('Error executing global text replacement command:', error);
      
      return false;
    } finally {
      // End batch operation regardless of success or failure
      this.editorCore.endBatch();
    }
  }

  /**
   * Undoes the global text replacement
   * 
   * @returns Promise resolving to true if successful
   */
  public async undo(): Promise<boolean> {
    
    try {
      // Start batch operation for better performance
      this.editorCore.beginBatch();

      // Revert all affected elements to their original values
      for (const affected of this.affectedElements) {
        this.editorCore.updateElementText(
          affected.segmentId,
          affected.paragraphId,
          affected.elementId,
          affected.originalValue,
          this.userId
        );
        
      }
      
      // Notify observers about the undo operation
      this.editorCore.notifyObservers(TranscriptEditorChangeType.TEXT_REPLACED_GLOBAL, {
        originalText: this.replacementText, // Note: these are swapped for undo
        replacementText: this.originalText, // Note: these are swapped for undo
        affectedElementsCount: this.affectedElements.length,
        replacementCount: this.replacementCount,
        isUndo: true
      });
      
      return true;
    } catch (error) {
      console.error('Error undoing global text replacement command:', error);
      
      return false;
    } finally {
      // End batch operation regardless of success or failure
      this.editorCore.endBatch();
    }
  }

  /**
   * Escapes special characters in the search string for regex
   * 
   * @param string The string to escape
   * @returns Escaped string safe for regex construction
   */
  private escapeRegExp(string: string): string {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
}